import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  PawPrint, Clock, Calendar, CheckCircle2, MessageCircle, MapPin,
  Menu, X, ArrowRight, HeartHandshake, Camera, ShieldCheck, Sparkles,
  Copy, Check, Phone, Share2, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import BookingForm from "@/components/BookingForm";
import { PRICING, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "@/lib/pricing";
import { trackWhatsAppClick } from "@/lib/analytics";

const WA_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Olá Cãomiar! Quero agendar um passeio para o meu pet 🐾"
)}`;

const NAV = [
  { href: "#servicos", label: "Serviços" },
  { href: "#precos", label: "Tabela" },
  { href: "#agendar", label: "Agendar" },
  { href: "#contato", label: "Contato" },
];

function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-brand-cream/80 border-b border-brand-cream" data-testid="nav">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
        <Logo height={44} />
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-semibold text-brand-green hover:text-brand-brown transition-colors" data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </a>
          ))}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp"
            className="inline-flex items-center gap-2 rounded-full bg-brand-whatsapp hover:bg-brand-whatsappDark text-white px-5 py-2.5 text-sm font-bold shadow-[0_6px_20px_rgba(37,211,102,0.35)] transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
        </nav>
        <button className="md:hidden text-brand-green" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-brand-cream border-t border-brand-creamDeep px-5 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="font-semibold text-brand-green">{n.label}</a>
          ))}
          <a href={WA_LINK} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("mobile-nav")} className="inline-flex items-center gap-2 rounded-full bg-brand-whatsapp text-white px-5 py-2.5 font-bold w-fit">
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="hero">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-brand-greenLight/25 blur-3xl" />
      <div className="absolute top-24 -left-24 w-[280px] h-[280px] rounded-full bg-brand-brown/15 blur-3xl" />
      <div className="paw-bg absolute inset-0 opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-white/70 border border-brand-green/15 text-brand-green rounded-full px-4 py-1.5 text-xs font-bold tracking-wider uppercase">
            <PawPrint size={14} /> Vila Mathias · Santos/SP
          </div>
          <h1 className="mt-5 font-display font-bold text-brand-green text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
            Passeador de cães <br />
            na <span className="italic text-brand-brown">Vila Mathias</span> — Santos.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-brand-brownDark max-w-xl">
            Seu cãozinho passa muito tempo sozinho? A <strong className="text-brand-green">Cãomiar</strong> oferece
            passeios seguros e recreativos para cães em <strong>Vila Mathias e bairros vizinhos</strong> de Santos/SP —
            mais saúde, energia gasta e felicidade para o seu melhor amigo.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#agendar"
              data-testid="hero-cta-agendar"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenDark text-brand-cream px-7 py-3.5 font-bold shadow-[0_10px_30px_rgba(44,110,73,0.32)] transition-transform hover:-translate-y-0.5"
            >
              Agendar um passeio <ArrowRight size={18} />
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("hero")}
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-green text-brand-green hover:bg-brand-green hover:text-brand-cream px-7 py-3.5 font-bold transition-colors"
            >
              <MessageCircle size={18} /> Falar no WhatsApp
            </a>
          </div>
          <div className="mt-10 flex items-center gap-6 text-brand-green">
            <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck size={18} /> Passeios seguros</div>
            <div className="flex items-center gap-2 text-sm font-semibold"><Camera size={18} /> Fotos ao vivo</div>
            <div className="flex items-center gap-2 text-sm font-semibold"><HeartHandshake size={18} /> Atendimento exclusivo</div>
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="relative">
            <div className="absolute -inset-6 bg-brand-greenLight/20 rounded-[40px] rotate-3" />
            <img
              alt="Passeio feliz na orla de Santos"
              src="/hero-santos.png"
              className="relative rounded-[36px] w-full h-[440px] md:h-[520px] object-cover shadow-[0_30px_80px_rgba(44,110,73,0.25)]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3 animate-float">
              <div className="w-11 h-11 rounded-xl bg-brand-green grid place-items-center text-brand-cream">
                <PawPrint size={22} />
              </div>
              <div>
                <div className="font-display font-bold text-brand-green text-xl leading-none">Passeios Felizes</div>
                <div className="text-xs text-brand-brownDark">em Vila Mathias · Santos</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  const items = [
    { icon: <HeartHandshake />, title: "Saúde e bem-estar", text: "Reduz o estresse, a ansiedade e o tédio do seu pet." },
    { icon: <ShieldCheck />, title: "Passeios seguros", text: "Roteiros adaptados à energia e ao porte do seu cão." },
    { icon: <Camera />, title: "Tranquilidade pra você", text: "Envio de fotos e vídeos durante todo o passeio." },
  ];
  return (
    <section id="servicos" className="relative py-20 md:py-28" data-testid="benefits">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-brown">Por que Cãomiar</div>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl text-brand-green">
            Cuidado que se sente em cada patinha.
          </h2>
          <p className="mt-4 text-brand-brownDark text-lg">
            Atendimento exclusivo do passeador de cães Cãomiar na <strong>Vila Mathias e bairros vizinhos</strong> de
            Santos/SP — Encruzilhada, Marapé, Campo Grande e região central.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {items.map((b, i) => (
            <div
              key={i}
              data-testid={`benefit-${i}`}
              className="group bg-white rounded-3xl p-8 border border-brand-cream shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_50px_rgba(44,110,73,0.15)] hover:-translate-y-1 transition-[transform,box-shadow]"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand-green text-brand-cream grid place-items-center mb-5 group-hover:rotate-[-6deg] transition-transform">
                {React.cloneElement(b.icon, { size: 26 })}
              </div>
              <h3 className="font-display text-2xl text-brand-green mb-2">{b.title}</h3>
              <p className="text-brand-brownDark">{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PriceCard({ dur, block, highlight }) {
  return (
    <div
      data-testid={`pricing-card-${dur}`}
      className={`relative rounded-3xl p-8 md:p-10 border transition-transform hover:-translate-y-1 ${
        highlight
          ? "bg-brand-green text-brand-cream border-brand-green shadow-[0_24px_60px_rgba(44,110,73,0.35)]"
          : "bg-white text-brand-green border-brand-cream shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 right-6 bg-brand-brown text-brand-cream text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">
          Mais popular
        </div>
      )}
      <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] ${highlight ? "text-brand-cream/80" : "text-brand-brown"}`}>
        <Clock size={14} /> Passeios de {block.duration}
      </div>

      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display font-bold text-6xl">R${block.avulso.price}</span>
        <span className={highlight ? "text-brand-cream/80" : "text-brand-brownDark"}>/ avulso</span>
      </div>

      <ul className="mt-8 space-y-3 text-[15px]">
        {block.semanal.map((s) => (
          <li key={s.key} className="flex items-start gap-3">
            <CheckCircle2 size={18} className={highlight ? "text-brand-cream mt-0.5" : "text-brand-greenLight mt-0.5"} />
            <span><strong>{s.label.split(" · ")[1]}</strong> — R$ {s.price} {s.unit}</span>
          </li>
        ))}
        {block.mensal.map((s) => (
          <li key={s.key} className="flex items-start gap-3">
            <Sparkles size={18} className={highlight ? "text-brand-cream mt-0.5" : "text-brand-brown mt-0.5"} />
            <span><strong>Plano Mensal {s.label.split(" · ")[1]}</strong> — R$ {s.price} {s.unit}</span>
          </li>
        ))}
      </ul>

      <a
        href="#agendar"
        data-testid={`pricing-cta-${dur}`}
        className={`mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold transition-transform hover:-translate-y-0.5 ${
          highlight
            ? "bg-brand-cream text-brand-green hover:bg-white"
            : "bg-brand-green text-brand-cream hover:bg-brand-greenDark"
        }`}
      >
        Escolher esse plano <ArrowRight size={16} />
      </a>
    </div>
  );
}

function Pricing() {
  return (
    <section id="precos" className="relative py-20 md:py-28 bg-brand-creamDeep/60" data-testid="pricing">
      <div className="paw-bg absolute inset-0 opacity-40 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-brown">Tabela de valores</div>
            <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl text-brand-green">
              Um plano pra cada rotina.
            </h2>
            <p className="mt-4 text-brand-brownDark text-lg">
              Preços transparentes, sem taxa escondida. Escolha entre passeios de 30 minutos ou 1 hora, avulsos, semanais ou mensais.
            </p>
          </div>
          <Link
            to="/panfletos"
            data-testid="link-panfletos"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brand-green text-brand-green px-5 py-2.5 font-bold text-sm hover:bg-brand-green hover:text-brand-cream transition-colors"
          >
            Baixar panfletos <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <PriceCard dur="30" block={PRICING["30"]} highlight={false} />
          <PriceCard dur="60" block={PRICING["60"]} highlight={true} />
        </div>
      </div>
    </section>
  );
}

function CopyPhoneButton() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(WHATSAPP_DISPLAY);
    } catch {
      // fallback for older browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = WHATSAPP_DISPLAY;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Número copiado!", { description: WHATSAPP_DISPLAY });
    setTimeout(() => setCopied(false), 2200);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="copy-phone-cta"
      className="group w-full flex items-center justify-between bg-white text-brand-green rounded-2xl px-6 py-5 border-2 border-brand-green/15 hover:border-brand-green hover:-translate-y-0.5 transition-[transform,border-color]"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-green/10 grid place-items-center text-brand-green">
          <Phone size={22} />
        </div>
        <div className="text-left">
          <div className="text-xs uppercase tracking-widest text-brand-brown">Telefone</div>
          <div className="font-display font-bold text-xl leading-none text-brand-green">{WHATSAPP_DISPLAY}</div>
        </div>
      </div>
      <span
        className={
          "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors " +
          (copied ? "bg-brand-green text-brand-cream" : "bg-brand-cream text-brand-green group-hover:bg-brand-green group-hover:text-brand-cream")
        }
      >
        {copied ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar</>}
      </span>
    </button>
  );
}

function ContactAndBooking() {
  return (
    <section id="agendar" className="py-20 md:py-28" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-brown">Contato</div>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl text-brand-green leading-tight">
            Vamos passear?
          </h2>
          <p className="mt-4 text-brand-brownDark text-lg">
            Entre em contato pelo WhatsApp para uma conversa rápida ou envie o formulário — a Gisele confirma o horário com você.
          </p>

          <div id="contato" className="mt-8 space-y-4">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("contact")}
              data-testid="contact-whatsapp-cta"
              className="group flex items-center justify-between bg-brand-whatsapp text-white rounded-2xl px-6 py-5 shadow-[0_12px_40px_rgba(37,211,102,0.35)] hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-center gap-4">
                <MessageCircle size={26} />
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-90">WhatsApp</div>
                  <div className="font-display font-bold text-2xl leading-none">{WHATSAPP_DISPLAY}</div>
                </div>
              </div>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </a>

            <CopyPhoneButton />

            <div className="flex items-center gap-4 bg-white rounded-2xl px-6 py-5 border border-brand-cream" data-testid="contact-location">
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 grid place-items-center text-brand-green">
                <MapPin size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-brown">Atendimento exclusivo</div>
                <div className="font-display font-bold text-xl text-brand-green">Vila Mathias — Santos/SP</div>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-white rounded-2xl px-6 py-5 border border-brand-cream">
              <div className="w-12 h-12 rounded-xl bg-brand-brown/10 grid place-items-center text-brand-brown">
                <Calendar size={22} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-brand-brown">Horários</div>
                <div className="font-display font-bold text-xl text-brand-green">Seg a Dom · 19h às 23h</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-brown">
                  <span className="text-base leading-none">🌙</span> Atendimento noturno exclusivo
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <BookingForm />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-brand-green text-brand-cream" data-testid="footer">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <LogoMark height={72} light={true} />
          <p className="mt-4 text-brand-cream/85 max-w-md">
            A nova cara da Gisele Dog Walker — passeios seguros, carinhosos e feitos sob medida para a rotina do seu melhor amigo.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-widest text-brand-cream/70 mb-3">Contato</div>
          <div>WhatsApp: {WHATSAPP_DISPLAY}</div>
          <div>Vila Mathias · Santos/SP</div>
        </div>
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-widest text-brand-cream/70 mb-3">Materiais</div>
          <div className="flex flex-col gap-2">
            <Link to="/panfletos" className="underline underline-offset-4 hover:text-white">Panfletos imprimíveis</Link>
            <a href="#compartilhar" className="underline underline-offset-4 hover:text-white">Compartilhar o site</a>
          </div>
        </div>
      </div>
      <div className="border-t border-brand-cream/15">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-5 text-sm text-brand-cream/70 flex flex-col md:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} Cãomiar Dog Walker. Todos os direitos reservados.</div>
          <div>Rebranding de Gisele Dog Walker</div>
        </div>
      </div>
    </footer>
  );
}

function ShareSite() {
  const [copied, setCopied] = useState(false);
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = `Olha que legal esse Dog Walker aqui em Santos: Cãomiar 🐾 ${siteUrl}`;
  const waShareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = siteUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Link copiado!", { description: siteUrl });
    setTimeout(() => setCopied(false), 2200);
  };

  const nativeShare = async (e) => {
    if (navigator.share) {
      e.preventDefault();
      try {
        await navigator.share({
          title: "Cãomiar Dog Walker",
          text: "Passeios seguros e felizes em Santos/SP 🐾",
          url: siteUrl,
        });
      } catch (_) { /* user cancelled */ }
    }
  };

  return (
    <section id="compartilhar" className="pb-20 md:pb-28" data-testid="share-section">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="relative overflow-hidden rounded-[36px] bg-brand-green text-brand-cream p-8 md:p-12 shadow-[0_24px_60px_rgba(44,110,73,0.35)]">
          <div className="paw-bg absolute inset-0 opacity-20 pointer-events-none" />
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-brand-greenLight/30 blur-3xl" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-brand-cream/80">
                <Share2 size={14} /> Ajude a Cãomiar
              </div>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight">
                Curtiu? Indique pra um<br /> amigo tutor de pet.
              </h2>
              <p className="mt-4 text-brand-cream/85 text-lg max-w-lg">
                Um passeio de boca-a-boca vai longe. Compartilhe o site com quem também quer ver o cãozinho feliz.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col gap-3">
              <a
                href={waShareUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  trackWhatsAppClick("share");
                  nativeShare(e);
                }}
                data-testid="share-whatsapp"
                className="inline-flex items-center justify-between gap-3 rounded-full bg-brand-whatsapp hover:bg-brand-whatsappDark text-white px-6 py-4 font-bold shadow-[0_10px_30px_rgba(37,211,102,0.35)] transition-transform hover:-translate-y-0.5"
              >
                <span className="inline-flex items-center gap-3">
                  <MessageCircle size={20} /> Compartilhar no WhatsApp
                </span>
                <ArrowRight size={18} />
              </a>
              <button
                type="button"
                onClick={copyLink}
                data-testid="share-copy-link"
                className={
                  "inline-flex items-center justify-between gap-3 rounded-full px-6 py-4 font-bold transition-[transform,background-color] hover:-translate-y-0.5 " +
                  (copied
                    ? "bg-brand-cream text-brand-green"
                    : "bg-brand-cream/10 border-2 border-brand-cream/40 text-brand-cream hover:bg-brand-cream hover:text-brand-green")
                }
              >
                <span className="inline-flex items-center gap-3">
                  {copied ? <Check size={20} /> : <Link2 size={20} />}
                  {copied ? "Link copiado!" : "Copiar link do site"}
                </span>
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
              <div className="text-xs text-brand-cream/70 text-center mt-1">
                {siteUrl.replace(/^https?:\/\//, "")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function FloatingWA() {
  return (
    <a
      href={WA_LINK}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackWhatsAppClick("floating")}
      data-testid="floating-whatsapp"
      className="fixed z-50 bottom-6 right-6 bg-brand-whatsapp hover:bg-brand-whatsappDark text-white rounded-full w-14 h-14 grid place-items-center shadow-[0_12px_30px_rgba(37,211,102,0.5)] hover:-translate-y-1 transition-transform"
      aria-label="Abrir WhatsApp"
    >
      <MessageCircle size={26} />
    </a>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-brand-cream text-foreground" data-testid="home-page">
      <Nav />
      <Hero />
      <Benefits />
      <Pricing />
      <ContactAndBooking />
      <ShareSite />
      <Footer />
      <FloatingWA />
    </div>
  );
}
